import { cn } from "@/lib/utils";
import { createPageAnalytics } from "common/analytics";
import { ChevronDown } from "lucide-react";
import React, { useState } from "react";
import ReactSmoothCollapse from "react-smooth-collapse";
import { useI18N } from "common/hooks";

interface OpeningHour {
  day: number;
  openTime: number;
  closeTime: number;
}

interface OpeningHoursProps {
  openingHours?: OpeningHour[];
  isLoading?: boolean;
  containerClassName?: string;
  showHeading?: boolean;
}

const OpeningHours: React.FC<OpeningHoursProps> = ({
  openingHours,
  isLoading,
  containerClassName,
  showHeading = true,
}) => {
  const i18n = useI18N();
  const [expanded, setExpanded] = useState(false);

  const toggleExpanded = () => {
    if (!expanded) {
      // opening_hour_action_button -> place_opening_hours_expanded (fires on expand only; this
      // component renders only on the place About tab). Stub event.
      createPageAnalytics("place").track("opening_hour_action_button", {});
    }

    setExpanded(!expanded);
  };

  const getCurrentTimeAsHHMM = (): number => {
    const now = new Date();
    return now.getHours() * 100 + now.getMinutes();
  };

  const isCurrentlyOpen = (): boolean => {
    const today = new Date().getDay();
    const openingHoursToday = openingHours?.find((hour) => hour.day === today);

    if (!openingHoursToday) {
      return false;
    }

    const currentTime = getCurrentTimeAsHHMM();
    const { openTime, closeTime } = openingHoursToday;

    // Handle venues that close after midnight (e.g., open 1800, close 0200)
    if (closeTime < openTime) {
      return currentTime >= openTime || currentTime < closeTime;
    }

    // Normal case: open and close on the same day
    return currentTime >= openTime && currentTime < closeTime;
  };

  const getOpenStatus = (): "Open" | "Closed" => {
    return isCurrentlyOpen() ? "Open" : "Closed";
  };

  const getTodaysOpeningHours = (): string => {
    const today = new Date().getDay(); // Gets today's day of the week as a number
    const openingHoursToday = openingHours?.find((hour) => hour.day === today);

    if (openingHoursToday) {
      const openTime = formatTime(openingHoursToday.openTime);
      const closeTime = formatTime(openingHoursToday.closeTime);
      return `${openTime} - ${closeTime}`;
    }
    return i18n.get("closedToday");
  };

  const formatTime = (time: number): string => {
    // Assuming the time is in HHMM format
    const hours = Math.floor(time / 100);
    const minutes = time % 100;
    const amPm = hours >= 12 ? "PM" : "AM";

    // Convert hour to 12-hour format
    const formattedHour = hours % 12 || 12; // Converts'0' to'12' for 12 AM

    return `${formattedHour.toString().padStart(2, "0")}:${minutes
      .toString()
      .padStart(2, "0")} ${amPm}`;
  };

  const weekdays = [
    i18n.get("sunday"),
    i18n.get("monday"),
    i18n.get("tuesday"),
    i18n.get("wednesday"),
    i18n.get("thursday"),
    i18n.get("friday"),
    i18n.get("saturday"),
  ];

  if (isLoading) {
    return <div className={containerClassName}>{i18n.get("loading")}</div>;
  }

  const today = new Date().getDay();

  return (
    <article className={cn("flex flex-col w-full", containerClassName)}>
      {showHeading && (
        <h3 className="label-s-medium text-fg-neutral-subtle min-h-[34px] flex items-center">
          {i18n.get("openingHours")}
        </h3>
      )}

      <div className="flex flex-col gap-1">
        {/* Current Status */}
        <div className="flex items-center gap-4 min-h-[44px]">
          <div
            className="flex items-center gap-1 cursor-pointer"
            onClick={toggleExpanded}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                toggleExpanded();
              }
            }}
            role="button"
            tabIndex={0}
            aria-expanded={expanded}
            aria-controls="hours-list"
            aria-label={`${getOpenStatus()}: ${getTodaysOpeningHours()}. ${i18n.get("toggleToViewFullOpeningHours")}`}
          >
            <span className="label-m-medium !text-fg-brand-subtle">
              {getOpenStatus()} • <span className="text-fg-neutral-strong">{getTodaysOpeningHours()}</span>
            </span>
            <ChevronDown
              size={20}
              className={cn(
                "text-fg-neutral-strong transition-transform",
                expanded && "rotate-180",
              )}
              aria-hidden="true"
            />
          </div>
        </div>

        {/* Hours List */}
        <ReactSmoothCollapse expanded={expanded}>
          <div id="hours-list" className="flex flex-col">
            {openingHours?.map((hour) => {
              const openTime = formatTime(hour.openTime);
              const closeTime = formatTime(hour.closeTime);
              const dayName = weekdays[hour.day];
              const isToday = hour.day === today;
              const textClass = isToday
                ? "label-m-semibold text-fg-neutral-strong"
                : "label-m-medium text-fg-neutral-subtle";

              return (
                <div
                  key={dayName}
                  className="flex items-center min-h-[24px]"
                  aria-label={`${dayName} ${openTime} - ${closeTime}`}
                >
                  <span
                    className={cn(textClass, "w-[100px]")}
                    aria-hidden="true"
                  >
                    {dayName}
                  </span>
                  <span className={textClass} aria-hidden="true">
                    {openTime} - {closeTime}
                  </span>
                </div>
              );
            })}
          </div>
        </ReactSmoothCollapse>
      </div>
    </article>
  );
};

export default OpeningHours;
